import { readAppVersion } from "./version";

const AUTO_REFRESH_SESSION_KEY = "expertloop.admin.auto-refresh-signature";

type AppSignature = {
	version: string | null;
	entrySrc: string | null;
};

export async function ensureAppFresh(): Promise<boolean> {
	if (!import.meta.env.PROD) return false;

	const remoteSignature = await readRemoteAppSignature();
	if (!remoteSignature || !shouldRefresh(readCurrentAppSignature(), remoteSignature)) return false;

	const refreshSignature = [remoteSignature.version, remoteSignature.entrySrc].filter(Boolean).join("|");
	if (sessionStorage.getItem(AUTO_REFRESH_SESSION_KEY) === refreshSignature) return false;

	sessionStorage.setItem(AUTO_REFRESH_SESSION_KEY, refreshSignature);
	const url = new URL(window.location.href);
	url.searchParams.set("app_refresh", String(Date.now()));
	window.location.replace(url.toString());
	return true;
}

async function readRemoteAppSignature(): Promise<AppSignature | null> {
	try {
		const url = new URL("/index.html", window.location.origin);
		url.searchParams.set("app_probe", String(Date.now()));
		const response = await fetch(url, { cache: "no-store", headers: { accept: "text/html" } });
		if (!response.ok) return null;
		return readDocumentSignature(new DOMParser().parseFromString(await response.text(), "text/html"));
	} catch {
		return null;
	}
}

function readCurrentAppSignature(): AppSignature {
	const documentSignature = readDocumentSignature(document);
	return { version: normalize(readAppVersion()) ?? documentSignature.version, entrySrc: documentSignature.entrySrc };
}

function readDocumentSignature(root: ParentNode): AppSignature {
	return {
		version: normalize(root.querySelector('meta[name="app-version"]')?.getAttribute("content") ?? null),
		entrySrc: normalizeEntry(root.querySelector('script[type="module"][src]')?.getAttribute("src") ?? null),
	};
}

function shouldRefresh(current: AppSignature, remote: AppSignature): boolean {
	return Boolean(
		(current.version && remote.version && current.version !== remote.version) ||
			(current.entrySrc && remote.entrySrc && current.entrySrc !== remote.entrySrc),
	);
}

function normalize(value: string | null): string | null {
	const normalized = value?.trim();
	return normalized && !normalized.includes("%") ? normalized : null;
}

function normalizeEntry(value: string | null): string | null {
	const normalized = normalize(value);
	if (!normalized) return null;
	const url = new URL(normalized, window.location.origin);
	return `${url.pathname}${url.search}`;
}
